/**
 * The listing.
 *
 * One row per fact, one column per field, in the order the spec names them —
 * no axes, no totals, no `showAs`. Almost none of `PivotTable` is reusable
 * here, and that is the honest answer rather than a missed abstraction: a pivot
 * renders a matrix addressed by two tuples, a listing renders an array. What
 * the two DO share is upstream of both, in the query they came from.
 *
 * The rows arrive in the order the query produced them and are printed in that
 * order. Nothing is sorted, grouped or de-duplicated here: two identical rows
 * are two entries, and collapsing them would be this component inventing an
 * aggregate the reader did not ask for.
 */
import type { ReportModel, ReportSpec, ResultRow } from '@clockwise/semantic-core';
import {
  defaultSortDir,
  dimText,
  findDimension,
  findMeasure,
  isAdditive,
} from '@clockwise/semantic-core';
import { DefinitionHoverCard } from '@clockwise/ui';
import { EMPTY_CELL, resolvedFormatter } from '../format';

interface Props {
  model: ReportModel;
  spec: ReportSpec;
  rows: readonly ResultRow[];
  /**
   * Called when a header is clicked. OMIT and headers render as plain text —
   * same rule as the pivot's: a placed tile must not drift from the order it
   * was saved with.
   */
  onSort?: (by: string) => void;
  /**
   * Measure id → its total over the WHOLE answer (`meta.totals`), not this
   * page. Supplied by the transport, because once a listing pages there is no
   * way to work it out from what is on screen.
   */
  totals?: Record<string, number | null>;
  /**
   * True when this page is not the whole answer. Only reached when a transport
   * sends no `totals` of its own — see the caption below.
   */
  paged?: boolean;
  /**
   * Measure id → its formula as the SERVER states it (`meta.formulas`); wins
   * over the model's `formulaText`, as the footer's ƒ definitions does.
   */
  formulas?: Readonly<Record<string, string>>;
}

/**
 * A column's classes, for a body cell or for its header.
 *
 * `nowrap` is deliberately NOT put on the header. Under `table-layout: auto` a
 * column is at least as wide as its widest unbreakable content, so one
 * unwrapping `td` already floors the column at a whole date — the header can
 * then wrap freely and cost nothing. Pinning it too would floor the column at
 * "Timesheet Date" on one line instead, which is wider than the dates it sits
 * over, and thirty columns each a few pixels too wide is what pushes the
 * listing off the side of its scrollport.
 */
function cellClass(
  field: { kind: 'dimension' | 'measure'; nowrap?: boolean },
  where: 'head' | 'body',
): string | undefined {
  if (field.kind === 'measure') return 'rk-num';
  return field.nowrap && where === 'body' ? 'rk-nowrap' : undefined;
}

export function DetailTable({ model, spec, rows, onSort, totals, paged, formulas }: Props) {
  /**
   * One resolution per COLUMN, memoised across the rows it renders.
   *
   * A column of hours is hours all the way down, so resolving per cell would
   * walk the model once per number on screen.
   */
  const resolvedCells = new Map<string, ReturnType<typeof resolvedFormatter>>();
  const cells = (id: string) => {
    let made = resolvedCells.get(id);
    if (!made) {
      made = resolvedFormatter(model, spec, id);
      resolvedCells.set(id, made);
    }
    return made;
  };

  const fields = spec.columns.map((id) => {
    const dimension = findDimension(model, id);
    if (dimension)
      return {
        id,
        label: dimension.label,
        kind: 'dimension' as const,
        // A DATE IS ONE LINE OR IT IS NOTHING. `2026-08-18` broken after the
        // month reads as two values, and it only breaks because a free-text
        // column beside it took the width. `isTime` is already the model's
        // word for "this dimension is a point in time", so the columns that
        // must not wrap are named by the model rather than listed here.
        nowrap: dimension.isTime === true,
      };
    const measure = findMeasure(model, id);
    return {
      id,
      label: measure?.label ?? id,
      kind: 'measure' as const,
      formula: formulas?.[id] ?? measure?.formulaText,
      description: measure?.description,
    };
  });

  const leadSort = spec.rowSort[0];

  /**
   * One total, and only for a column that can be added.
   *
   * The pivot's three totals do not exist here: there is no column axis to
   * total across and no grand corner where two meet. `isAdditive` is the same
   * honesty rule the pivot uses — an average or a ratio summed down a column
   * is a confident wrong number — and a dimension column simply has nothing to
   * total, so its cell is blank rather than an em dash pretending it does.
   *
   * It is the total of the WHOLE answer and it comes from the transport, which
   * is the only place that can see all of it once the listing pages. Adding up
   * what is on screen would total one page and label it "Total" — or, as it
   * once did, "Total of these 500", which is the same wrong number with a
   * caption nobody on page 7 can act on.
   *
   * The page sum survives only as a fallback for a transport that sends no
   * totals, and then only says "Total" when this page IS the whole answer.
   */
  const showTotals = spec.totals.col || spec.totals.row;
  const fromPage = totals === undefined;
  const totalOf = (id: string): number | null => {
    const measure = findMeasure(model, id);
    if (!measure || !isAdditive(measure)) return null;
    if (totals) return totals[id] ?? null;
    let sum = 0;
    let seen = false;
    for (const row of rows) {
      const v = row.values[id];
      if (v === null || v === undefined) continue;
      sum += v;
      seen = true;
    }
    return seen ? sum : null;
  };
  // "of this page" only when a page sum is actually what is printed. With no
  // additive column there is nothing to sum either way — a row of em dashes
  // captioned as a page total draws a distinction that does not exist here.
  const sums = fields.some((f) => {
    const m = f.kind === 'measure' ? findMeasure(model, f.id) : undefined;
    return m !== undefined && isAdditive(m);
  });
  const caption = fromPage && paged && sums ? 'Total of this page' : 'Total';

  return (
    // `rk-detail-scroll` is what makes the header pinnable: it caps the height
    // so the listing scrolls in a region of its own, which is the scrollport
    // the sticky `thead` and the sticky total row below both resolve against.
    // Without a cap this box is `overflow-y: auto` at content height — a scroll
    // container that never scrolls, so `position: sticky` inside it has nowhere
    // to travel and silently does nothing.
    <div className="rk-table-scroll rk-detail-scroll">
      <table className="rk-table rk-detail">
        <thead>
          <tr>
            {fields.map((field) => {
              const sorted = leadSort?.by === field.id ? leadSort.dir : undefined;
              const head = onSort ? (
                <button
                  type="button"
                  className="rk-sortable"
                  // A measure's formula shows when its label is pointed
                  // at, and the arrow beside the label says "sortable"
                  // without needing prose.
                  title={field.kind === 'dimension' ? `Sort by ${field.label}` : undefined}
                  onClick={() => onSort(field.id)}
                >
                  {field.label}
                  {/* ALWAYS rendered, dimmed until this column decides the
                  order. Two things follow: the header advertises that
                  it is sortable before anyone has clicked it — a
                  10px uppercase label reads as a caption, not a
                  control — and the column keeps its width when the
                  order changes, instead of the whole table reflowing
                  sideways as the mark appears. The idle glyph points
                  the way a click would actually sort: down for a
                  measure ("biggest first"), up for a name. */}
                  <span
                    className={sorted ? 'rk-sortmark' : 'rk-sortmark rk-sortmark-idle'}
                    aria-hidden="true"
                  >
                    {(sorted ?? defaultSortDir(model, field.id)) === 'asc' ? '▲' : '▼'}
                  </span>
                </button>
              ) : (
                field.label
              );
              return (
                <th
                  key={field.id}
                  scope="col"
                  className={cellClass(field, 'head')}
                  // Only the LEAD entry of the chain gets the marker: a column
                  // that merely breaks another's ties did not decide this order,
                  // and marking it would say the table is sorted by something it
                  // mostly is not.
                  aria-sort={
                    sorted === undefined ? undefined : sorted === 'asc' ? 'ascending' : 'descending'
                  }
                >
                  {field.kind === 'measure' ? (
                    // The description, formula underneath, ON DEMAND from
                    // the label itself — no glyph on the header.
                    <DefinitionHoverCard description={field.description} formula={field.formula}>
                      <span className="rk-headtip">{head}</span>
                    </DefinitionHoverCard>
                  ) : (
                    head
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            // A listing has no key of its own — two entries can agree on every
            // column shown and still be two entries — and the rows are replaced
            // wholesale on each query rather than reordered in place, so there
            // is no reconciliation a better key would protect.
            // biome-ignore lint/suspicious/noArrayIndexKey: position is the identity here
            <tr key={index}>
              {fields.map((field) => (
                <td
                  key={field.id}
                  className={[
                    cellClass(field, 'body'),
                    field.kind === 'measure'
                      ? cells(field.id).className(row.values[field.id])
                      : undefined,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  // The whole text on hover, for a cell narrow enough to cut it
                  // — the drill tray keeps its rows to one line.
                  title={
                    field.kind === 'dimension' ? longText(dimText(row.dims[field.id])) : undefined
                  }
                >
                  {field.kind === 'dimension'
                    ? // The label, falling back to the key: a listing shows the
                      // name a reader recognises, not the id it was joined on.
                      dimText(row.dims[field.id]) || EMPTY_CELL
                    : cells(field.id).format(row.values[field.id])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {showTotals && (
          <tfoot>
            <tr className="rk-totalrow">
              {/* The caption goes in the FIRST cell whatever that cell is. It
                  used to be printed only by the dimension branch, so a listing
                  whose first column was a measure rendered a row of bare
                  numbers. A measure in first position carries both: the caption
                  floats left, the number stays right-aligned under its column. */}
              {fields.map((field, i) => {
                if (field.kind === 'dimension') {
                  return (
                    <th key={field.id} scope="row" className="rk-rowhead rk-total">
                      {i === 0 && caption}
                    </th>
                  );
                }
                return (
                  <td
                    key={field.id}
                    className={['rk-num rk-total', cells(field.id).className(totalOf(field.id))]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {i === 0 && <span className="rk-total-caption">{caption}</span>}
                    {cells(field.id).format(totalOf(field.id))}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

/** A cell's text as a tooltip, when it is long enough to have been cut. */
const longText = (text: string | null | undefined) => (text && text.length > 40 ? text : undefined);
